import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { RotinaGrupoCriado, RotinaGrupoSalvo, UltimaExecucao } from '@/services/api/rotinas';
import { CAMPOS_PROXIMA, STATUS_EXECUCAO, dataLocal, diaFalado, hhmm } from './rotinaUtils';

function Dispensar({ onFechar }: { onFechar: () => void }) {
    return (
        <button
            type="button"
            className="mt-1 text-xs underline underline-offset-2 opacity-80 hover:opacity-100"
            onClick={onFechar}
        >
            Dispensar
        </button>
    );
}

function Contador({ rotulo, valor, destaque }: { rotulo: string; valor: number | null; destaque?: boolean }) {
    if (valor === null) return null;
    return (
        <li className={destaque ? 'font-medium text-amber-700' : undefined}>
            {valor} {rotulo}
        </li>
    );
}

export function UltimaExecucaoInfo({ u }: { u: UltimaExecucao | null | undefined }) {
    if (u === undefined) return null;
    if (u === null) {
        return (
            <span className="text-muted-foreground">
                Última execução: <span className="font-medium text-foreground">nunca rodou</span>
            </span>
        );
    }
    const quando = new Date(u.criado_em.replace(' ', 'T'))
        .toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    const ruim = u.status === 'parcial' || u.status === 'falha';
    return (
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-muted-foreground">
            <span>
                Última execução:{' '}
                <span className={`font-medium ${ruim ? 'text-destructive' : 'text-foreground'}`}>
                    {u.data.split('-').reverse().join('/')} — {STATUS_EXECUCAO[u.status]}
                </span>
            </span>
            <span className="tabular-nums">
                ({u.grupos_criados} grupo(s), {u.broadcasts_criados} broadcast(s)
                {u.paginas_puladas > 0 && (
                    <span className="font-medium text-amber-700">, {u.paginas_puladas} página(s) pulada(s)</span>
                )}
                {u.paginas_puladas === 0 && ', 0 página pulada'})
            </span>
            <span className="text-xs">registrada {quando}</span>
            {u.status === 'ok' && u.grupos_criados === 0 && (
                <span className="text-xs text-amber-700">
                    rodou e não criou nada (Rotina vazia ou toda pausada)
                </span>
            )}
            {u.paginas_puladas > 0 && (
                <span className="text-xs text-amber-700">pausadas ou ausentes, não receberam disparo</span>
            )}
            {u.status === 'executando' && (
                <span className="text-xs">ainda não terminou; os números podem crescer</span>
            )}
            {u.erro && <span className="text-xs font-medium text-destructive">Erro: {u.erro}</span>}
            {/* Aviso em bloco próprio e recolhido: são um por grupo, e dez deles
                em vermelho faziam uma execução bem-sucedida parecer incidente. */}
            {u.avisos && (
                <details className="mt-1 rounded-lg border bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
                    <summary className="cursor-pointer select-none">
                        {u.avisos.split('; ').length} observação(ões) da materialização — não são erro
                    </summary>
                    <ul className="mt-1 list-disc space-y-0.5 pl-4">
                        {u.avisos.split('; ').map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                </details>
            )}
        </span>
    );
}

export function ResultadoDaEdicao({ r, onFechar }: { r: RotinaGrupoSalvo; onFechar: () => void }) {
    return (
        <div className="space-y-2 text-xs">
            {r.erro_propagacao && (
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Grupo salvo, mas a propagação falhou</AlertTitle>
                    <AlertDescription>
                        {r.erro_propagacao}. Parte dos broadcasts de hoje (ou de amanhã, se já criados) pode ter sido alterada: confira antes de reeditar.
                    </AlertDescription>
                </Alert>
            )}

            {!r.erro_propagacao && !r.propagou && (
                <p className="rounded-md border bg-muted/40 p-2">
                    Salvo. Nenhum campo desta edição é propagável, então nada foi tentado nos broadcasts já criados.
                </p>
            )}

            {!r.erro_propagacao && r.propagou && (
                <div className="rounded-md border bg-muted/40 p-2">
                    <p className="font-medium">
                        {r.atualizados === 0
                            ? 'Propagação tentada e nada casou: 0 broadcasts atualizados.'
                            : `Propagação aos broadcasts já criados: ${r.atualizados} atualizado(s).`}
                    </p>
                    <ul className="mt-1 list-disc pl-4">
                        <Contador rotulo="já iniciados, não alterados" valor={r.ignorados_ja_iniciados} />
                        <Contador rotulo="agendados no passado (disparam na hora)" valor={r.agendados_no_passado} destaque={!!r.agendados_no_passado} />
                        <Contador rotulo="com tradução do mapa" valor={r.com_traducao_do_mapa} />
                        <Contador rotulo="sem tradução no mapa (mantêm a tradução antiga)" valor={r.sem_traducao_no_mapa} destaque={!!r.sem_traducao_no_mapa} />
                        <Contador rotulo="sem page.id, mantidos intactos" valor={r.sem_page_id} destaque={!!r.sem_page_id} />
                        <Contador rotulo="com erro, mantidos intactos" valor={r.com_erro} destaque={!!r.com_erro} />
                    </ul>
                </div>
            )}

            {r.aviso_traducao && (
                <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Traduções não foram refeitas</AlertTitle>
                    <AlertDescription>{r.aviso_traducao}</AlertDescription>
                </Alert>
            )}

            {r.valem_na_proxima_materializacao.length > 0 && (
                <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900">
                    Só valem a partir da próxima materialização (não chegam aos broadcasts já criados):{' '}
                    {r.valem_na_proxima_materializacao.map((c) => CAMPOS_PROXIMA[c] ?? c).join(', ')}.
                </p>
            )}

            {/* "Não vale nunca" é diferente de "vale na próxima": sem esta linha, quem
                cravou app_id para fugir do 613 acharia que resolveu. */}
            {r.campos_ignorados?.length > 0 && (
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>
                        Gravado no grupo e ignorado na hora de disparar:{' '}
                        {r.campos_ignorados.map((c) => CAMPOS_PROXIMA[c] ?? c).join(', ')}
                    </AlertTitle>
                    <AlertDescription>
                        <ul className="list-disc space-y-1 pl-4">
                            {r.campos_ignorados.map((c) => (
                                <li key={c}>{r.motivo_ignorados?.[c] ?? 'não chega a nenhum broadcast.'}</li>
                            ))}
                        </ul>
                    </AlertDescription>
                </Alert>
            )}

            <Dispensar onFechar={onFechar} />
        </div>
    );
}

/** O POST devolve 201 mesmo com `erro_disparo_hoje`: o grupo existe, só os disparos do dia não. */
export function ResultadoDaCriacao({ r, onFechar }: { r: RotinaGrupoCriado; onFechar: () => void }) {
    const dia = r.data_disparos ? diaFalado(dataLocal(r.data_disparos)) : 'hoje';
    if (r.erro_disparo_hoje) {
        return (
            <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Grupo salvo, mas os disparos de {dia} NÃO foram criados</AlertTitle>
                <AlertDescription>
                    <p>
                        O grupo das {hhmm(r.hora_local)} está gravado, mas para {dia} ele não agendou nada, ou agendou só parte.
                        Confira na tela de Broadcasts antes de recriar: recriar o grupo duplica o que já tiver nascido.
                    </p>
                    <p className="mt-1 break-all font-mono text-xs">{r.erro_disparo_hoje}</p>
                    {/* Páginas puladas continuam valendo mesmo com erro: parte pode ter nascido. */}
                    <ul className="mt-1 list-disc pl-4 text-xs">
                        <Contador rotulo={`disparo(s) de ${dia} criado(s) antes da falha`} valor={r.broadcasts_criados_hoje || null} />
                        <Contador rotulo="agendados no passado (disparam na hora)" valor={r.agendados_no_passado || null} destaque />
                        <Contador rotulo="página(s) pulada(s): pausadas ou ausentes, não receberam disparo" valor={r.paginas_puladas || null} destaque />
                        <Contador rotulo="disparo(s) que nasceram com falha e não vão sair" valor={r.broadcasts_falhos || null} destaque />
                    </ul>
                    <Dispensar onFechar={onFechar} />
                </AlertDescription>
            </Alert>
        );
    }
    return (
        <div className="space-y-1 rounded-md border bg-muted/40 p-2 text-xs">
            <p className="font-medium">
                Grupo das {hhmm(r.hora_local)} salvo.{' '}
                {r.broadcasts_criados_hoje > 0
                    ? `${r.broadcasts_criados_hoje} disparo(s) de ${dia} criado(s).`
                    : 'Nenhum disparo criado agora: a Rotina está desligada, o dia dela ainda não foi materializado ou nenhuma página do grupo está ativa. Ele entra na materialização das 20h da véspera do próximo dia da Rotina.'}
            </p>
            {/* Página pulada e broadcast failed são páginas que NÃO recebem. Sem estas
                duas linhas a tela diria "173 criados" e calaria sobre as outras 100. */}
            <ul className="list-disc pl-4">
                <Contador rotulo="agendados no passado (disparam na hora)" valor={r.agendados_no_passado || null} destaque />
                <Contador rotulo="página(s) pulada(s): pausadas ou ausentes, não receberam disparo" valor={r.paginas_puladas || null} destaque />
                <Contador rotulo="disparo(s) que nasceram com falha e não vão sair" valor={r.broadcasts_falhos || null} destaque />
            </ul>
            <Dispensar onFechar={onFechar} />
        </div>
    );
}
